import proCardStyles from '@components/DataDisplay/ProCard/ProCard.module.scss'
import cn from '@integrations/classnames'
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState
} from '@integrations/react'
import { Link } from '@integrations/routing'
import { TransactionStatusCell, TransactionTypeCell } from '@transactions/components'
import tableStyles from '@transactions/components/TransactionsTable/TransactionsTable.module.scss'
import { getTransactionAmountSign, getTransactionDisplayAmount } from '@transactions/utils/transactionAmount'
import { formatDisplayDateTime, uiSize } from '@utils'
import {
  extractCounterpartyName,
  formatTransactionDescription,
  paths,
  TransactionDirections,
  TransactionStatuses,
  useCurrencies,
  useGetTransactionsQuery
} from 'mmfintech-backend-api'
import type { TransactionHistoryFilterRequest, TransactionViewPortal } from 'mmfintech-backend-api/types'
import { formatMoney, isValidArray, tr } from 'mmfintech-commons'
import { ErrorDisplay, MMTableView, Spinner } from 'mmfintech-portal-commons'

const LATEST_TRANSACTIONS_COUNT = 10
const COMPACT_TRANSACTIONS_COUNT = 5
const TABLE_LAYOUT_QUERY = '(min-width: 800px)'

interface Props {
  /** Without an account: the latest transactions across every account the user can see. */
  accountId?: number
  /** How many to fetch; with `fitHeight`, the most that can show. */
  size?: number
  /** Replaces the one-line empty message, e.g. with an illustration and next steps. */
  empty?: ReactNode
  /** Shows only the rows that fit the height the host gives the table. */
  fitHeight?: boolean
  /** Below the table layout: only the newest few. */
  compact?: boolean
  /**
   * A title for the list, shown only when there are transactions to list: not on a first load, on error or when empty.
   * It stays while a list that has rows refetches, so it does not blink out and back in.
   * Rendered as a sibling above the list: the host owns the spacing between the two.
   */
  heading?: ReactNode
}

/** The latest transactions, newest first, every direction and status. */
function AccountTransactions({
  accountId,
  size = LATEST_TRANSACTIONS_COUNT,
  empty,
  fitHeight,
  compact,
  heading
}: Props) {
  const { getCurrencyPrecision } = useCurrencies()
  const containerRef = useRef<HTMLDivElement>(null)
  const [fitCount, setFitCount] = useState<number>()

  const { transactions, transactionsError, transactionsFetching } = useGetTransactionsQuery(
    {
      accountId,
      page: 0,
      size,
      sort: 'reqTime-desc,id-desc'
    } as TransactionHistoryFilterRequest,
    {
      selectFromResult: ({ data, error, isFetching }) => ({
        transactions: data?.content,
        transactionsError: error,
        transactionsFetching: isFetching
      })
    }
  )

  useLayoutEffect(() => {
    const container = containerRef.current
    if ((!fitHeight && !compact) || !container || typeof ResizeObserver === 'undefined') return

    function measure() {
      if (!window.matchMedia?.(TABLE_LAYOUT_QUERY).matches) {
        setFitCount(compact ? COMPACT_TRANSACTIONS_COUNT : undefined)
        return
      }
      const head = container.querySelector('thead')
      const row = container.querySelector<HTMLElement>('tbody tr')
      const footer = container.querySelector<HTMLElement>('.account-transactions-footer')
      if (!fitHeight || !head || !row) {
        setFitCount(undefined)
        return
      }
      const available = container.clientHeight - head.offsetHeight - (footer?.offsetHeight ?? 0)
      setFitCount(Math.max(1, Math.floor(available / row.offsetHeight)))
    }

    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
  }, [fitHeight, compact, transactions])

  // The full record lives on the Transactions page; a new tab keeps this account in view.
  function openDetails(transactionId: number) {
    window.open(paths.banking.transactions.details(transactionId), '_blank', 'noopener,noreferrer')
  }

  // Middle click opens a tab like it would on a link.
  function handleRowAuxClick(event: MouseEvent, transactionId: number) {
    if (event.button === 1) openDetails(transactionId)
  }

  function handleRowKeyDown(event: KeyboardEvent, transactionId: number) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openDetails(transactionId)
    }
  }

  if (transactionsFetching) {
    return (
      <>
        {isValidArray(transactions) && heading}
        <Spinner />
      </>
    )
  }

  if (transactionsError) {
    return <ErrorDisplay error={transactionsError} />
  }

  if (!isValidArray(transactions)) {
    if (empty) return empty

    return (
      <p className={cn(proCardStyles['pro-card__empty'], 'pro-card__empty')} data-test='account-transactions-empty'>
        {tr('FRONTEND.ACCOUNTS.TRANSACTIONS.EMPTY', 'No transactions on this account yet.')}
      </p>
    )
  }

  return (
    <>
      {heading}
      <div
        ref={containerRef}
        className={cn(
          tableStyles['transactions-table-container'],
          'transactions-table-container',
          'table-surface',
          'table-row-cards'
        )}
        data-test='account-transactions'
      >
        <MMTableView.Table>
          <MMTableView.Head>
            <MMTableView.Row>
              <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.DATE_ONLY', 'Date')}</MMTableView.HeadCell>
              <MMTableView.HeadCell>
                {tr('FRONTEND.TRANSACTIONS.COLUMNS.TRANSACTION_TYPE', 'Type')}
              </MMTableView.HeadCell>
              <MMTableView.HeadCell>
                {tr('FRONTEND.TRANSACTIONS.COLUMNS.DESCRIPTION', 'Description')}
              </MMTableView.HeadCell>
              <MMTableView.HeadCell className={cn('amount-cell')}>
                {tr('FRONTEND.TRANSACTIONS.COLUMNS.AMOUNT', 'Amount')}
              </MMTableView.HeadCell>
              <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.STATUS', 'Status')}</MMTableView.HeadCell>
            </MMTableView.Row>
          </MMTableView.Head>

          <MMTableView.Body>
            {transactions.slice(0, fitCount).map((data: TransactionViewPortal) => {
              const { id, reqTime, direction, status, trnTypeLocalizationKey } = data
              const { amount, currency } = getTransactionDisplayAmount(data)
              const description =
                formatTransactionDescription(data) || extractCounterpartyName(data) || data.reference || '—'
              const isFailed = status === TransactionStatuses.FAILED
              const isIncoming = direction === TransactionDirections.DEPOSIT && !isFailed

              return (
                <MMTableView.Row
                  key={id}
                  className={cn('clickable')}
                  onClick={() => openDetails(id)}
                  onAuxClick={(event: MouseEvent) => handleRowAuxClick(event, id)}
                  onKeyDown={(event: KeyboardEvent) => handleRowKeyDown(event, id)}
                  tabIndex={0}
                  data-test='account-transaction-row'
                >
                  <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.DATE_ONLY', 'Date')}>
                    <span className={cn('text-nowrap')}>{formatDisplayDateTime(reqTime)}</span>
                  </MMTableView.Cell>
                  <MMTableView.Cell
                    caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.TRANSACTION_TYPE', 'Type')}
                    className={cn('row-primary-cell')}
                  >
                    <TransactionTypeCell direction={direction} trnTypeLocalizationKey={trnTypeLocalizationKey} />
                  </MMTableView.Cell>
                  <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.DESCRIPTION', 'Description')}>
                    <span className={cn(tableStyles['ellipsis'], 'ellipsis')} title={description}>
                      {description}
                    </span>
                  </MMTableView.Cell>
                  <MMTableView.Cell
                    caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.AMOUNT', 'Amount')}
                    className={cn('amount-cell', 'row-primary-cell')}
                  >
                    <span
                      className={cn(tableStyles['amount'], 'amount', {
                        [tableStyles['muted']]: isFailed,
                        muted: isFailed,
                        [tableStyles['incoming']]: isIncoming,
                        incoming: isIncoming
                      })}
                      data-test='account-transaction-amount'
                    >
                      {getTransactionAmountSign(direction, status)}
                      {formatMoney(amount, currency, getCurrencyPrecision(currency)).trim()}
                    </span>
                  </MMTableView.Cell>
                  <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.STATUS', 'Status')}>
                    <TransactionStatusCell status={status} />
                  </MMTableView.Cell>
                </MMTableView.Row>
              )
            })}
          </MMTableView.Body>
        </MMTableView.Table>

        {accountId && (
          <Link
            to={paths.banking.transactions.listByAccountId(accountId)}
            className={cn(
              proCardStyles['pro-card__footer-link'],
              'pro-card__footer-link',
              'account-transactions-footer'
            )}
            data-test='account-transactions-see-all'
          >
            {tr('FRONTEND.ACCOUNTS.TRANSACTIONS.SEE_ALL', 'See all transactions for this account')}
            <svg
              width={uiSize(12)}
              height={uiSize(12)}
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              <path d='M5 12h14M12 5l7 7-7 7' />
            </svg>
          </Link>
        )}
      </div>
    </>
  )
}

export default AccountTransactions
